import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { DateRange } from '@enums/enum';
import * as moment from 'moment';
import { AnalyticsService } from '@analytics/services/analytics.service';
import { Observable, Subject } from 'rxjs';
import { StatsRoutingDistances } from '@analytics/interfaces/stats-routing-distances.interface';
import { TranslateService } from '@ngx-translate/core';
import enLocale from 'date-fns/locale/en-GB';
import plLocale from 'date-fns/locale/pl';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-stats-routing-distances',
  templateUrl: './stats-routing-distances.component.html',
  styleUrls: ['./stats-routing-distances.component.scss']
})
export class StatsRoutingDistancesComponent implements OnInit, OnDestroy {

  public loader: boolean = true;
  public startDate;
  public endDate;
  public dateRange: DateRange;
  public Permissions = Permissions;
  public locale = null;
  public defaultDateRange: DateRange = DateRange.LAST_30_DAYS;
  public aggregation: 'DAYS' | 'WEEKS' | 'MONTHS' = "DAYS";

  public lineChartSeries: {name: string, series: {name: string; value: number | string}[]}[] = [
    {name: this.translateService.instant('Distance'), series: [] },
    {name: this.translateService.instant('Average dry boxes'), series: [] },
    {name: this.translateService.instant('Average chilled boxes'), series: [] },
    {name: this.translateService.instant('Average frozen boxes'), series: [] }
  ]

  public barChart: {name: string, value: number}[] = [];

  public view = [null, null];
  public showXAxis = true;
  public showYAxis = true;
  public gradient = false;
  public showLegend = false;
  public legendTitle = 'Legend';
  public legendPosition = 'right';
  public showXAxisLabel = false;
  public xAxisLabel = 'Country';
  public showYAxisLabel = true;
  public yAxisLabel = this.translateService.instant('Deliveries');
  public showGridLines = true;
  public innerPadding = '10%';
  public animations: boolean = true;

  public lineChartScheme = {
    name: 'coolthree',
    selectable: true,
    group: 'Ordinal',
    domain: ['#fce9b8', '#e5ceb7', '#5AA454', '#9fdfea']
  };

  public comboBarScheme = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'Ordinal',
    domain: ['#dfe3e9']
  };

  public showRightYAxisLabel: boolean = true;
  public yAxisLabelRight: string = `${this.translateService.instant('Distance')} km.`;

  private destroy$ = new Subject<boolean>();
  public entity$: Observable<StatsRoutingDistances[]>;

  public tableMessages = {
    emptyMessage: this.translateService.instant('No data to display')
  };

  public columns = [
    { prop: 'date', name: this.translateService.instant('Date'), flexGrow: 2 },
    { prop: 'deliveries', name: this.translateService.instant('Deliveries'), flexGrow: 2 },
    { prop: 'averageDryBoxCount', name: this.translateService.instant('averageDryBoxCount'), flexGrow: 2 },
    { prop: 'averageChilledBoxCount', name: this.translateService.instant('averageChilledBoxCount'), flexGrow: 2 },
    { prop: 'averageFrozenBoxCount', name: this.translateService.instant('averageFrozenBoxCount'), flexGrow: 2 },
    { prop: 'distance', name: this.translateService.instant('Distance'), flexGrow: 2 },
    { prop: 'averageDistance', name: this.translateService.instant('Avarage distance'), flexGrow: 2 },
  ];


  constructor(
    private readonly analyticsService: AnalyticsService,
    private readonly translateService: TranslateService
  ) {}

  public ngOnInit() {
    this.locale = this.translateService.currentLang === 'en' ? enLocale : plLocale;
  }

  public changedDateRange(event) {
    this.loader = true;
    this.startDate = moment(event.dateStart).format('YYYY-MM-DD');
    this.endDate = moment(event.dateEnd).format('YYYY-MM-DD');
    this.dateRange = event.dateRange;
    this.getStats();
  }

  public changedAggregation(aggregation: 'DAYS' | 'WEEKS' | 'MONTHS') {
    this.aggregation = aggregation;
    this.getStats();
  }

  private getStats() {
    this.entity$ = this.analyticsService.getRoutingDistancesStats(this.startDate, this.endDate, this.aggregation).pipe(
      takeUntil(this.destroy$),
      map((statsResults: StatsRoutingDistances[]) => {

        this.lineChartSeries[0].series = [];
        this.barChart = [];
        

        statsResults.map((s: StatsRoutingDistances) => {
          let name = '';

          switch (this.aggregation) {
            case 'MONTHS' :
              name = moment(s.date).format('MM-YYYY')
            break;
            case 'WEEKS' :
              name = `${s.date} - ${moment(s.date).add(7, 'days').format('YYYY-MM-DD')}`
            break;
            default: 
              name = s.date;
          }

          this.lineChartSeries[0].series.push({
            name: name,
            value: (s.averageDistance / 1000).toFixed(2)
          });

          this.lineChartSeries[1].series.push({
            name: name,
            value: s.averageDryBoxCount
          });

          this.lineChartSeries[2].series.push({
            name: name,
            value: s.averageChilledBoxCount
          });

          this.lineChartSeries[3].series.push({
            name: name,
            value: s.averageFrozenBoxCount
          });
          
          this.barChart.push({name: name, value: s.deliveries});

          s.date = name;
          
        });

        this.loader = false;
        setTimeout(() => {
          this.updateChartSize();
        }, 200)
        return statsResults;
      })
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.updateChartSize();
  }

  private updateChartSize(): void {
    const containerWidth = document.querySelector('.chart-container')?.clientWidth || 800;
    this.view = [containerWidth, 400]; // Aktualizuje szerokość do szerokości kontenera
  }

  public ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
